import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '../../components/common/Footer';
import {
  PageContainer,
  TopHeader,
  LogoSection,
  NavButtons,
  NavButton,
  NavButton2,
  MainContainer,
  CardsSection,
  CardGrid,
  LegalCard,
  CardIcon,
  CardTitle,
  CardDescription,
  // Desktop Components
  DesktopLayout,
  Sidebar,
  SidebarSection,
  SidebarTitle,
  SidebarItem,
  ContentArea,
  // Mobile Components
  MobileLayout,
  MobileMenuButton,
  MobileMenuOverlay,
  MobileSidebar,
  MobileMenuHeader,
  MobileMenuTitle,
  MobileCloseButton,
  MobileContentArea
} from './styles';
import logoImage from '../../assets/images/logo.png';

// Lazy loading de todos os componentes
const TerminosGenerales = lazy(() => import('./components/documentos/TerminosGenerales'));
const PoliticaUsoResponsable = lazy(() => import('./components/documentos/PoliticaUsoResponsable'));
const TerminosCompra = lazy(() => import('./components/documentos/TerminosCompra'));
const PoliticaPrivacidad = lazy(() => import('./components/documentos/PoliticaPrivacidad'));
const PoliticaCookies = lazy(() => import('./components/documentos/PoliticaCookies'));
const CookiesAmpliada = lazy(() => import('./components/documentos/CookiesAmpliada'));
const Comunicaciones = lazy(() => import('./components/documentos/Comunicaciones'));
const SeguridadInformacion = lazy(() => import('./components/documentos/SeguridadInformacion'));
const VerificacionIdentidad = lazy(() => import('./components/documentos/VerificacionIdentidad'));
const AutorizacionFinanciera = lazy(() => import('./components/documentos/AutorizacionFinanciera'));
const ModificacionDatos = lazy(() => import('./components/documentos/ModificacionDatos'));
const PagoContraentrega = lazy(() => import('./components/documentos/PagoContraentrega'));
const VendedoresPagos = lazy(() => import('./components/documentos/VendedoresPagos'));
const PoliticaPago = lazy(() => import('./components/documentos/PoliticaPago'));
const FormasPago = lazy(() => import('./components/documentos/FormasPago'));
const Envios = lazy(() => import('./components/documentos/Envios'));
const Devoluciones = lazy(() => import('./components/documentos/Devoluciones'));
const RetencionImpuestos = lazy(() => import('./components/documentos/RetencionImpuestos'));
const ObligacionesVendedores = lazy(() => import('./components/documentos/ObligacionesVendedores'));
const ResponsabilidadCoproduccion = lazy(() => import('./components/documentos/ResponsabilidadCoproduccion'));
const TerminosCoproduccion = lazy(() => import('./components/documentos/TerminosCoproduccion'));
const ActualizacionContenidos = lazy(() => import('./components/documentos/ActualizacionContenidos'));
const FraudesSuplantaciones = lazy(() => import('./components/documentos/FraudesSuplantaciones'));
const ContenidoPermitido = lazy(() => import('./components/documentos/ContenidoPermitido'));
const SuspensionProductos = lazy(() => import('./components/documentos/SuspensionProductos'));
const CierreAdministrativo = lazy(() => import('./components/documentos/CierreAdministrativo'));
const Interoperabilidad = lazy(() => import('./components/documentos/Interoperabilidad'));
const LicenciaLimitada = lazy(() => import('./components/documentos/LicenciaLimitada'));
const AfiliacionPromocion = lazy(() => import('./components/documentos/AfiliacionPromocion'));
const ProgramaIndicacion = lazy(() => import('./components/documentos/ProgramaIndicacion'));
const MarketingRemarketing = lazy(() => import('./components/documentos/MarketingRemarketing'));
const PromocionesSorteos = lazy(() => import('./components/documentos/PromocionesSorteos'));
const CancelacionSuspension = lazy(() => import('./components/documentos/CancelacionSuspension'));
const ResolucionConflictos = lazy(() => import('./components/documentos/ResolucionConflictos'));
const PropiedadIntelectual = lazy(() => import('./components/documentos/PropiedadIntelectual'));
const OpinionesTestimonios = lazy(() => import('./components/documentos/OpinionesTestimonios'));
const CodigoEtico = lazy(() => import('./components/documentos/CodigoEtico'));
const ResponsabilidadLimitada = lazy(() => import('./components/documentos/ResponsabilidadLimitada'));
const RecaudoPagos = lazy(() => import('./components/documentos/RecaudoPagos'));
const EstadosOperativos = lazy(() => import('./components/documentos/EstadosOperativos'));
const ContratoMaestro = lazy(() => import('./components/documentos/ContratoMaestro'));
const CodigoAcceso = lazy(() => import('./components/documentos/CodigoAcceso'));
const AfiliadosCoproduccion = lazy(() => import('./components/documentos/AfiliadosCoproduccion'));
const ConsentimientoUnificado = lazy(() => import('./components/documentos/ConsentimientoUnificado'));
const InfluenciadoresEmbajadores = lazy(() => import('./components/documentos/InfluenciadoresEmbajadores'));
const JurisdiccionInternacional = lazy(() => import('./components/documentos/JurisdiccionInternacional'));
const GlosarioLegal = lazy(() => import('./components/documentos/GlosarioLegal'));
const VersionPublica = lazy(() => import('./components/documentos/VersionPublica'));

const CentroLegal = () => {
  const [selectedContent, setSelectedContent] = useState('terminos-generales');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (section) {
      setSelectedContent(section);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  const legalCards = [
    {
      id: 'terminos-generales',
      icon: '📋',
      title: 'Términos Generales',
      description: 'Condiciones generales de uso de la plataforma Floqss'
    },
    {
      id: 'privacidad',
      icon: '🔒',
      title: 'Política de Privacidad',
      description: 'Cómo protegemos y manejamos tus datos personales'
    },
    {
      id: 'cookies',
      icon: '🍪',
      title: 'Política de Cookies',
      description: 'Información sobre el uso de cookies en nuestra plataforma'
    },
    {
      id: 'devoluciones',
      icon: '↩️',
      title: 'Devoluciones',
      description: 'Política de devoluciones y garantías de productos'
    }
  ];

  const documentComponents = {
    'terminos-generales': TerminosGenerales,
    'politica-uso-responsable': PoliticaUsoResponsable,
    'terminos-compra': TerminosCompra,
    'privacidad': PoliticaPrivacidad,
    'cookies': PoliticaCookies,
    'cookies-ampliada': CookiesAmpliada,
    'comunicaciones': Comunicaciones,
    'seguridad-informacion': SeguridadInformacion,
    'verificacion-identidad': VerificacionIdentidad,
    'autorizacion-financiera': AutorizacionFinanciera,
    'modificacion-datos': ModificacionDatos,
    'pago-contraentrega': PagoContraentrega,
    'vendedores-pagos': VendedoresPagos,
    'politica-pago': PoliticaPago,
    'formas-pago': FormasPago,
    'envios': Envios,
    'devoluciones': Devoluciones,
    'retencion-impuestos': RetencionImpuestos,
    'obligaciones-vendedores': ObligacionesVendedores,
    'responsabilidad-coproduccion': ResponsabilidadCoproduccion,
    'terminos-coproduccion': TerminosCoproduccion,
    'actualizacion-contenidos': ActualizacionContenidos,
    'fraudes-suplantaciones': FraudesSuplantaciones,
    'contenido-permitido': ContenidoPermitido,
    'suspension-productos': SuspensionProductos,
    'cierre-administrativo': CierreAdministrativo,
    'interoperabilidad': Interoperabilidad,
    'licencia-limitada': LicenciaLimitada,
    'afiliacion-promocion': AfiliacionPromocion,
    'programa-indicacion': ProgramaIndicacion,
    'marketing-remarketing': MarketingRemarketing,
    'promociones-sorteos': PromocionesSorteos,
    'cancelacion-suspension': CancelacionSuspension,
    'resolucion-conflictos': ResolucionConflictos,
    'propiedad-intelectual': PropiedadIntelectual,
    'opiniones-testimonios': OpinionesTestimonios,
    'codigo-etico': CodigoEtico,
    'responsabilidad-limitada': ResponsabilidadLimitada,
    'recaudo-pagos': RecaudoPagos,
    'estados-operativos': EstadosOperativos,
    'contrato-maestro': ContratoMaestro,
    'codigo-acceso': CodigoAcceso,
    'afiliados-coproduccion': AfiliadosCoproduccion,
    'consentimiento-unificado': ConsentimientoUnificado,
    'influenciadores-embajadores': InfluenciadoresEmbajadores,
    'jurisdiccion-internacional': JurisdiccionInternacional,
    'glosario-legal': GlosarioLegal,
    'version-publica': VersionPublica
  };

  const sidebarItems = {
    principais: [
      { id: 'terminos-generales', title: 'Términos Generales de Uso' },
      { id: 'politica-uso-responsable', title: 'Política de Uso Responsable' },
      { id: 'privacidad', title: 'Política de Privacidad' },
      { id: 'cookies', title: 'Política de Cookies' },
      { id: 'cookies-ampliada', title: 'Cookies Ampliada' },
      { id: 'comunicaciones', title: 'Comunicaciones y Notificaciones' },
      { id: 'seguridad-informacion', title: 'Seguridad de la Información' },
      { id: 'verificacion-identidad', title: 'Verificación de Identidad' },
      { id: 'autorizacion-financiera', title: 'Autorización Financiera' },
      { id: 'modificacion-datos', title: 'Modificación de Datos' }
    ],
    comerciales: [
      { id: 'terminos-compra', title: 'Términos de Compra' },
      { id: 'pago-contraentrega', title: 'Pago Contra Entrega' },
      { id: 'politica-pago', title: 'Política de Pagos' },
      { id: 'formas-pago', title: 'Formas de Pago' },
      { id: 'envios', title: 'Política de Envíos' },
      { id: 'devoluciones', title: 'Devoluciones y Garantías' },
      { id: 'retencion-impuestos', title: 'Retención de Impuestos' },
      { id: 'recaudo-pagos', title: 'Recaudo y Pagos' },
      { id: 'estados-operativos', title: 'Estados Operativos' }
    ],
    productores: [
      { id: 'vendedores-pagos', title: 'Vendedores - Pagos' },
      { id: 'obligaciones-vendedores', title: 'Obligaciones Vendedores' },
      { id: 'responsabilidad-coproduccion', title: 'Responsabilidad Coproducción' },
      { id: 'terminos-coproduccion', title: 'Términos Coproducción' },
      { id: 'actualizacion-contenidos', title: 'Actualización de Contenidos' },
      { id: 'contenido-permitido', title: 'Contenido Permitido' },
      { id: 'suspension-productos', title: 'Suspensión de Productos' },
      { id: 'afiliacion-promocion', title: 'Afiliación y Promoción' },
      { id: 'programa-indicacion', title: 'Programa de Indicación' },
      { id: 'marketing-remarketing', title: 'Marketing y Remarketing' },
      { id: 'promociones-sorteos', title: 'Promociones y Sorteos' },
      { id: 'contrato-maestro', title: 'Contrato Maestro' },
      { id: 'afiliados-coproduccion', title: 'Afiliados y Coproducción' },
      { id: 'influenciadores-embajadores', title: 'Influenciadores y Embajadores' }
    ],
    seguridad: [
      { id: 'fraudes-suplantaciones', title: 'Fraudes y Suplantaciones' },
      { id: 'cierre-administrativo', title: 'Cierre Administrativo' },
      { id: 'cancelacion-suspension', title: 'Cancelación y Suspensión' },
      { id: 'codigo-acceso', title: 'Código de Acceso' },
      { id: 'codigo-etico', title: 'Código Ético' },
      { id: 'consentimiento-unificado', title: 'Consentimiento Unificado' }
    ],
    legal: [
      { id: 'interoperabilidad', title: 'Interoperabilidad' },
      { id: 'licencia-limitada', title: 'Licencia Limitada' },
      { id: 'resolucion-conflictos', title: 'Resolución de Conflictos' },
      { id: 'propiedad-intelectual', title: 'Propiedad Intelectual' },
      { id: 'opiniones-testimonios', title: 'Opiniones y Testimonios' },
      { id: 'responsabilidad-limitada', title: 'Responsabilidad Limitada' },
      { id: 'jurisdiccion-internacional', title: 'Jurisdicción Internacional' },
      { id: 'glosario-legal', title: 'Glosario Legal' },
      { id: 'version-publica', title: 'Versión Pública Condensada' }
    ]
  };

  const handleCardClick = (cardId) => {
    setSelectedContent(cardId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSidebarClick = (itemId) => {
    setSelectedContent(itemId);
    setIsMobileMenuOpen(false); // Fecha o menu mobile após seleção
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderContent = () => {
    const Component = documentComponents[selectedContent];

    if (Component) {
      return (
        <Suspense fallback={
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            borderRadius: '12px',
            color: '#94a3b8'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <img src={logoImage} alt="Floqss" style={{ width: '60px', height: '60px', marginBottom: '1rem' }} />
            </div>
            <p>Carregando documento...</p>
          </div>
        }>
          <Component />
        </Suspense>
      );
    }

    return (
      <div style={{
        padding: '3rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        borderRadius: '12px',
        color: '#e2e8f0'
      }}>
        <div style={{ marginBottom: '2rem' }}>
          <img src={logoImage} alt="Floqss" style={{ width: '60px', height: '60px', marginBottom: '1rem' }} />
        </div>
        <h3 style={{ color: '#3b82f6', marginBottom: '1rem' }}>Documento em Preparação</h3>
        <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>Este documento legal está sendo preparado e estará disponível em breve.</p>

        <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#1e293b', borderRadius: '8px' }}>
          <h4 style={{ color: '#3b82f6', marginBottom: '1rem' }}>Contacto</h4>
          <p style={{ color: '#e2e8f0', margin: '0.5rem 0' }}>📧 contacto@floqss.co</p>
          <p style={{ color: '#e2e8f0', margin: '0.5rem 0' }}>📱 WhatsApp: +57 304 204 1209</p>
          <p style={{ color: '#e2e8f0', margin: '0.5rem 0' }}>🌐 www.floqss.co</p>
        </div>
      </div>
    );
  };

  const renderSidebarSections = () => {
    return (
      <>
        <SidebarSection>
          <SidebarTitle>📋 Políticas Principais</SidebarTitle>
          {(sidebarItems.principais || []).map(item => (
            <SidebarItem
              key={item.id}
              onClick={() => handleSidebarClick(item.id)}
              style={{
                cursor: 'pointer',
                backgroundColor: selectedContent === item.id ? '#3b82f6' : 'transparent',
                color: selectedContent === item.id ? '#ffffffff' : '#B8C5D1',
                fontWeight: selectedContent === item.id ? '600' : 'normal'
              }}
            >
              {item.title}
            </SidebarItem>
          ))}
        </SidebarSection>

        <SidebarSection>
          <SidebarTitle>💼 Términos Comerciales</SidebarTitle>
          {(sidebarItems.comerciales || []).map(item => (
            <SidebarItem
              key={item.id}
              onClick={() => handleSidebarClick(item.id)}
              style={{
                cursor: 'pointer',
                backgroundColor: selectedContent === item.id ? '#3b82f6' : 'transparent'
              }}
            >
              {item.title}
            </SidebarItem>
          ))}
        </SidebarSection>

        <SidebarSection>
          <SidebarTitle>🎯 Para Productores</SidebarTitle>
          {(sidebarItems.productores || []).map(item => (
            <SidebarItem
              key={item.id}
              onClick={() => handleSidebarClick(item.id)}
              style={{
                cursor: 'pointer',
                backgroundColor: selectedContent === item.id ? '#3b82f6' : 'transparent'
              }}
            >
              {item.title}
            </SidebarItem>
          ))}
        </SidebarSection>

        <SidebarSection>
          <SidebarTitle>🔒 Seguridad</SidebarTitle>
          {(sidebarItems.seguridad || []).map(item => (
            <SidebarItem
              key={item.id}
              onClick={() => handleSidebarClick(item.id)}
              style={{
                cursor: 'pointer',
                backgroundColor: selectedContent === item.id ? '#3b82f6' : 'transparent'
              }}
            >
              {item.title}
            </SidebarItem>
          ))}
        </SidebarSection>

        <SidebarSection>
          <SidebarTitle>⚖️ Marco Legal</SidebarTitle>
          {(sidebarItems.legal || []).map(item => (
            <SidebarItem
              key={item.id}
              onClick={() => handleSidebarClick(item.id)}
              style={{
                cursor: 'pointer',
                backgroundColor: selectedContent === item.id ? '#3b82f6' : 'transparent'
              }}
            >
              {item.title}
            </SidebarItem>
          ))}
        </SidebarSection>
      </>
    );
  };

  return (
    <>
      <PageContainer>
        <TopHeader>
          <LogoSection>
            <a href="/" rel="noreferrer">
              <img src={logoImage} alt="Floqss" />
            </a>
            <div>
              <h1>Centro Legal FLOQSS</h1>
            </div>
          </LogoSection>
          <NavButtons>
            <NavButton2>🔒 Seguro</NavButton2>
            <NavButton active>📋 Legal Center</NavButton>
          </NavButtons>
        </TopHeader>

        <MainContainer>
          <CardsSection>
            <CardGrid>
              {legalCards.map(card => (
                <LegalCard
                  key={card.id}
                  onClick={() => handleCardClick(card.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <CardIcon>{card.icon}</CardIcon>
                  <CardTitle>{card.title}</CardTitle>
                  <CardDescription>{card.description}</CardDescription>
                </LegalCard>
              ))}
            </CardGrid>
          </CardsSection>

          {/* ===== VERSÃO DESKTOP (ORIGINAL) ===== */}
          <DesktopLayout>
            <Sidebar>
              {renderSidebarSections()}
            </Sidebar>
            <ContentArea>
              {renderContent()}
            </ContentArea>
          </DesktopLayout>

          {/* ===== VERSÃO MOBILE (SEPARADA) ===== */}
          <MobileLayout>
            <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)}>
              📋 Menu Legal
            </MobileMenuButton>

            <MobileMenuOverlay
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            <MobileSidebar isOpen={isMobileMenuOpen}>
              <MobileMenuHeader>
                <MobileMenuTitle>Centro Legal</MobileMenuTitle>
                <MobileCloseButton onClick={() => setIsMobileMenuOpen(false)}>
                  ✕
                </MobileCloseButton>
              </MobileMenuHeader>
              {renderSidebarSections()}
            </MobileSidebar>

            <MobileContentArea>
              {renderContent()}
            </MobileContentArea>
          </MobileLayout>
        </MainContainer>
      </PageContainer>
      <Footer />
    </>
  );
};

export default CentroLegal;